import type { Difficulty } from './types';

export const LEADERBOARD_DIFFICULTIES: Difficulty[] = [
  'beginner', 'trader', 'cannonhorse', 'gambiteer', 'attacker', 'defender',
  'pawn', 'miser', 'blockader', 'scholar', 'veteran', 'master',
];

// This is a game-design strength ladder, not a published human or association Elo.
export const FREE_OPPONENT_RATINGS: Record<Difficulty, number> = {
  beginner: -300,
  trader: -175,
  cannonhorse: -50,
  gambiteer: 50,
  attacker: 140,
  defender: 220,
  pawn: 300,
  miser: 380,
  blockader: 460,
  scholar: 540,
  veteran: 640,
  master: 760,
};

export type RankedResult = 'win' | 'draw' | 'loss';

export const UNDO_SCORE_PERCENT = 20;

export function resultValue(result: RankedResult): number {
  return result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;
}

export function freeRatingDelta(
  currentRating: number,
  gamesPlayed: number,
  difficulty: Difficulty,
  result: RankedResult,
): number {
  const opponentRating = FREE_OPPONENT_RATINGS[difficulty];
  const expected = 1 / (1 + 10 ** ((opponentRating - currentRating) / 400));
  const k = gamesPlayed < 10 ? 36 : 24;
  return Math.round(k * (resultValue(result) - expected));
}

export function storyResultRank(result: RankedResult): number {
  return result === 'win' ? 2 : result === 'draw' ? 1 : 0;
}

export function storyBestScore(difficulty: Difficulty, result: RankedResult): number {
  const stage = LEADERBOARD_DIFFICULTIES.indexOf(difficulty) + 1;
  if (result === 'win') return 100 + stage * 25;
  if (result === 'draw') return 25 + stage * 5;
  return 0;
}

export function scoreAfterUndos(baseScore: number, undosUsed: number): number {
  if (baseScore <= 0) return baseScore;
  const chargedUndos = Math.max(0, Math.floor(undosUsed));
  return Math.max(0, Math.round(baseScore * (1 - chargedUndos * UNDO_SCORE_PERCENT / 100)));
}

export function storyBestScoreAfterUndos(difficulty: Difficulty, result: RankedResult, undosUsed: number): number {
  return scoreAfterUndos(storyBestScore(difficulty, result), undosUsed);
}

export function overallScore(storyScore: number, freeRating: number): number {
  return storyScore + freeRating;
}

export function storyReputation(storyScore: number): number {
  return storyScore;
}
